import React, { useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import {
  buildServiceVoucherData,
  type VoucherOrderSource,
} from '../../utils/voucher/buildServiceVoucherData';
import { generateServiceVoucherPdf } from '../../utils/voucher/generateServiceVoucherPdf';

type Props = {
  order: VoucherOrderSource;
  size?: 'sm' | 'md';
  className?: string;
  /** Show both View (open) and Download, or a single compact download. */
  mode?: 'full' | 'download-only';
};

const VoucherActions: React.FC<Props> = ({
  order,
  size = 'sm',
  className,
  mode = 'full',
}) => {
  const [busy, setBusy] = useState<'download' | 'open' | null>(null);

  const run = async (output: 'download' | 'open') => {
    if (busy) return;
    setBusy(output);
    try {
      const data = buildServiceVoucherData(order);
      await generateServiceVoucherPdf(data, { output });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Service voucher PDF failed', err);
      window.alert('Could not generate the service voucher. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  if (mode === 'download-only') {
    return (
      <Button
        color="success"
        size={size}
        outline
        className={className}
        disabled={!!busy}
        onClick={() => void run('download')}
        title="Download service voucher"
      >
        {busy ? <Spinner size="sm" /> : 'Voucher'}
      </Button>
    );
  }

  return (
    <div className={`d-flex flex-wrap gap-1 ${className || ''}`}>
      <Button
        color="primary"
        outline
        size={size}
        disabled={!!busy}
        onClick={() => void run('open')}
      >
        {busy === 'open' ? <Spinner size="sm" /> : 'View Voucher'}
      </Button>
      <Button
        color="success"
        size={size}
        disabled={!!busy}
        onClick={() => void run('download')}
      >
        {busy === 'download' ? <Spinner size="sm" /> : 'Download Voucher'}
      </Button>
    </div>
  );
};

export default VoucherActions;
